// EditFeedModal.tsx

import React, { useState, useEffect, useRef } from "react";
import { Modal, TextInput, Button, Group, Image, Text, Select } from "@mantine/core";
import classes from "./edit.module.css";
import { RowDataMain } from "./types";
import { Dropzone } from "@mantine/dropzone";
import { IconPhoto, IconUpload, IconVideo } from "@tabler/icons-react";
import useAxiosPrivate from "../../auth/useAxiosPrivate";

interface EditFeedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEditFeed: (editedFeedData: RowDataMain) => void;
  feedData: RowDataMain;
}

const EditFeedModal: React.FC<EditFeedModalProps> = ({
  isOpen,
  onClose,
  onEditFeed,
  feedData,
}) => {
  const [editedFeed, setEditedFeed] = useState<RowDataMain>(feedData);
  const videoDropzoneRef = useRef<() => void>(null);
  const thumbnailDropzoneRef = useRef<() => void>(null);
  const axiosPrivate = useAxiosPrivate();
  const [catList, setCatList] = useState<string[]>([]);

  const fetchCategory = async () => {
    try {
      const response = await axiosPrivate.get("/api/feed/read-category"); 
      const resDataArr = response.data.data;
      setCatList(["", resDataArr[0].name, resDataArr[1].name, resDataArr[2].name])
    } catch (err) {
      console.error("Failed to fetch contact [ERROR]:", err);
    } finally {
    }
  };

  const handleVideoUpload = (files: File[]) => {
    const file = files[0];
    if (file) {
      // const url = URL.createObjectURL(file);
    //   console.log(
    //     !editedFeed.video.path
    //       ? editedFeed.video
    //       : URL.createObjectURL(editedFeed.video)
    //   );
      setEditedFeed({ ...editedFeed, video: file });
    }
    // console.log(editedFeed.video.path);
  };

  const handleThumbnailUpload = (files: File[]) => {
    const file = files[0];
    if (file) {
      // const url = URL.createObjectURL(file);
      setEditedFeed({ ...editedFeed, thumbnail: file });
    }
  };

  // Update the state when feedData changes
  useEffect(() => {
    fetchCategory()
    setEditedFeed(feedData);
  }, [feedData]);

  const handleEdit = () => {
    // Perform validation or additional logic as needed
    onEditFeed(editedFeed);
    // console.log(editedFeed)
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center", fontWeight: "bold" }}>Edit Feed</div>
      }
      opened={isOpen}
      onClose={onClose}
    >
      <TextInput
        label="Title"
        maxLength={255}
        mb="sm"
        required
        value={editedFeed.title}
        onChange={(event) =>
          setEditedFeed({ ...editedFeed, title: event.currentTarget.value })
        }
      />

      <Select
          label="Category"
          placeholder="Select Category"
          required
          mb="sm"
          value={editedFeed.category?.toString() || ""}
          data={[
              { value: "1", label: catList[1] },
              { value: "2", label: catList[2] },
              { value: "3", label: catList[3] },
          ]}
          onChange={(value) =>{
            setEditedFeed({...editedFeed, category: value? Number(value) : 0});
          }
        }
      />

      <Text className={classes.Text}>
        Video <span style={{ color: 'red' }}>*</span> <span className={classes.format}>(.mp4)</span>
      </Text>
      <Dropzone
        onDrop={handleVideoUpload}
        accept={["video/*"]}
        openRef={videoDropzoneRef}
        multiple={false}
      >
        <div className={classes.videoContainer}>
          {editedFeed.video ? (
            <video
              key={
                !editedFeed.video.path
                  ? editedFeed.video.toString()
                  : editedFeed.video.path
              }
              controls
              className={classes.videoSize}
            >
              <source
                src={
                  !editedFeed.video.path
                    ? editedFeed.video
                    : URL.createObjectURL(editedFeed.video)
                }
                type="video/mp4"
              />
              The content can't support
            </video>
          ) : (
            <p>
              <IconVideo />
            </p>
          )}
        </div>
      </Dropzone>

      <Group>
        <Button
          onClick={() => videoDropzoneRef.current?.()}
          className={classes.videoButton}
        >
          <IconUpload />
          Upload Video
        </Button>
      </Group>

      <Text className={classes.Text}>
        Thumbnail <span style={{ color: 'red' }}>*</span> <span className={classes.format}>(.jpg, .png)</span>
      </Text>
      <Dropzone
        onDrop={handleThumbnailUpload}
        accept={["image/*"]}
        openRef={thumbnailDropzoneRef}
        multiple={false}
      >
        <div className={classes.thumbnailContainer}>
          {editedFeed.thumbnail ? (
            <Image
              src={
                !editedFeed.thumbnail.path
                  ? editedFeed.thumbnail
                  : URL.createObjectURL(editedFeed.thumbnail)
              }
              alt="Thumbnail"
              className={classes.thumbnailSize}
            />
          ) : (
            <p>
              <IconPhoto />
            </p>
          )}
        </div>
      </Dropzone>
      <Group>
        <Button
          onClick={() => thumbnailDropzoneRef.current?.()}
          className={classes.thumbnailButton}
        >
          <IconPhoto />
          Upload Thumbnail
        </Button>
      </Group>

      <Group className={classes.buttonBottom}>
        <Button onClick={() => onClose()} className={classes.buttonCancel}>
          Back
        </Button>
        <Button onClick={handleEdit} className={classes.buttonSaveChanges}>
          Save Changes
        </Button>
      </Group>
    </Modal>
  );
};

export default EditFeedModal;
